import { styled } from '@mui/material'
import {
  $AnchorableShape,
  $CornerFixingHoles,
  $Hole,
  $KickPlateFixingHoles,
  CircularHole,
  Hole,
} from 'metalworks-domain'
import type { AnchorPoint } from 'metalworks-domain'
import { useObservableEagerState } from 'observable-hooks'
import { useCallback, useMemo } from 'react'
import { map } from 'rxjs'

import { assertExistence } from '@salsita-ai/platform-common/utils'
import { selectableComponent } from '@salsita-ai/platform-project-sdk/domain'
import { TooltipWithArrow, SelectionHotspot as UiSelectionHotspot } from '@salsita-ai/platform-project-sdk/ui'

import { anchorPointsService } from '../systems/anchor-points-service'
import { configuratorInterface } from '../systems/configurator-interface'

const AnchorPointHotspotDefault = styled(UiSelectionHotspot)(({ theme }) => ({
  height: 10,
  width: 10,
  paddingInline: 0,
  borderColor: theme.palette.error.main,
}))

const AnchorPointHotspotSelected = styled(UiSelectionHotspot)(({ theme }) => ({
  height: 15,
  width: 15,
  paddingInline: 0,
  borderColor: theme.palette.error.main,
}))

const AnchorPointHotspotSelectedOrigin = styled(UiSelectionHotspot)(({ theme }) => ({
  height: 20,
  width: 20,
  paddingInline: 0,
  borderColor: theme.palette.error.main,
  backgroundColor: theme.palette.error.main,
}))

const AnchorPointHotspotLocal = styled(UiSelectionHotspot)(({ theme }) => ({
  height: 10,
  width: 10,
  paddingInline: 0,
  borderColor: theme.palette.success.main,
  backgroundColor: theme.palette.success.main,
}))

const AnchorPointHotspotSelectedLocal = styled(UiSelectionHotspot)(({ theme }) => ({
  height: 20,
  width: 20,
  paddingInline: 0,
  borderColor: theme.palette.success.main,
  backgroundColor: theme.palette.success.main,
}))

const AnchorPointHotspotUnvailable = styled(UiSelectionHotspot)(({ theme }) => ({
  height: 10,
  width: 10,
  paddingInline: 0,
  borderColor: theme.palette.grey[500],
  backgroundColor: theme.palette.grey[500],
}))

interface Props {
  id: AnchorPoint['id']
}

export const AnchorPointHotspot = (props: Props) => {
  const { id } = props
  const { navigationSystem, storeSystem, componentReader, domain } = configuratorInterface.useDomain()

  const { visible: anchorPointsVisible } = useObservableEagerState(anchorPointsService.state$)

  const activeState = useObservableEagerState(navigationSystem.activeNavigationState$)

  const isSelectable$ = useMemo(() => {
    return storeSystem.stateSlices.committed.pipe(
      map(emission => {
        const selectableComponents = componentReader.readManyComponents(
          {
            entityIds: [id],
            componentTypes: [selectableComponent],
          },
          emission.state
        )

        if (selectableComponents.length > 1) {
          throw new Error(
            `Expected to have at most one selectable component for given navigation state ${activeState}, got ${selectableComponents.length}`
          )
        }

        return selectableComponents.at(0) != null
      })
    )
  }, [componentReader, id, storeSystem.stateSlices.committed, activeState])

  const isSelectable = useObservableEagerState(isSelectable$)
  const selectedEntityId = useObservableEagerState(navigationSystem.selectedEntityId$)

  const selectedEntity = Hole.isId(selectedEntityId)
    ? domain.get$Entity(selectedEntityId, storeSystem.stateSlices.committed.value.state)
    : null

  const isAnySelectedEntity = !!selectedEntity
  const { $absCoords, $shape } = configuratorInterface.useEntityState(id)

  const isSelectedEntityAFeature = selectedEntity && $Hole.is(selectedEntity)

  const canSwitchAnchorPoints =
    isAnySelectedEntity && !$CornerFixingHoles.is(selectedEntity) && !$KickPlateFixingHoles.is(selectedEntity)
  const isSelectedEntity = selectedEntityId === id
  const isSelectedEntityOriginAnchor =
    selectedEntity && $Hole.is(selectedEntity) && selectedEntity.anchorPointToId === id
  const isSelectedEntityLocalAnchor =
    selectedEntity && $Hole.is(selectedEntity) && selectedEntity.anchorPointFromId === id

  const isLocalToSelectedEntity =
    !!selectedEntity && $Hole.is(selectedEntity) && selectedEntity.$anchorPoints.some(ap => ap.id === id)

  const isCircularAnchor =
    selectedEntity &&
    $AnchorableShape.is(selectedEntity) &&
    $shape.$anchorableShapesChain.some(shape => shape.id === selectedEntity.id)

  const handleIgnoredAnchorPointClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    // block de-selection
    e.stopPropagation()
  }, [])

  const handleLocalAnchorPointClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation()

      if (!isSelectedEntityAFeature) {
        return
      }

      // change the selected local anchor point to this one
      const currentLocalAnchorPoint = assertExistence(selectedEntity.$anchorPointFrom)
      const deltaX = $absCoords.x - currentLocalAnchorPoint.$absCoords.x
      const deltaY = $absCoords.y - currentLocalAnchorPoint.$absCoords.y

      const update = configuratorInterface.updates.DomainEntityModification({
        entityId: selectedEntity.id,
        updateTag: 'feature.changeLocalAnchorPoint',
        stateDelta: {
          anchorPointFromId: id,
          anchorPointToId: selectedEntity.anchorPointToId,
          x: selectedEntity.x + deltaX,
          y: selectedEntity.y + deltaY,
        },
      })
      storeSystem.stageAndCommit([update])
    },
    [$absCoords, id, isSelectedEntityAFeature, selectedEntity, storeSystem]
  )

  const handleOriginAnchorPointClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation()

      if (!isSelectedEntityAFeature) {
        return
      }

      // change the selected origin anchor point to this one
      const currentOriginAnchorPoint = assertExistence(selectedEntity.$anchorPointTo)
      const deltaX = currentOriginAnchorPoint.$absCoords.x - $absCoords.x
      const deltaY = currentOriginAnchorPoint.$absCoords.y - $absCoords.y

      const update = configuratorInterface.updates.DomainEntityModification({
        entityId: selectedEntity.id,
        updateTag: 'feature.changeOriginAnchorPoint',
        stateDelta: {
          anchorPointFromId: selectedEntity.anchorPointFromId,
          anchorPointToId: id,
          x: selectedEntity.x + deltaX,
          y: selectedEntity.y + deltaY,
          ...(CircularHole.is(selectedEntity) ? { radius: selectedEntity.radius } : {}),
        },
      })
      storeSystem.stageAndCommit([update])
    },
    [$absCoords, id, selectedEntity, isSelectedEntityAFeature, storeSystem]
  )

  if (!anchorPointsVisible) {
    return null
  }

  if (!isSelectable || !isAnySelectedEntity || !canSwitchAnchorPoints) {
    return null
  }

  if (isSelectedEntity) {
    return <AnchorPointHotspotSelected id={id} />
  }

  if (isCircularAnchor) {
    return (
      <TooltipWithArrow title="This feature is already positioned relative to selected feature." placement="top">
        <AnchorPointHotspotUnvailable id={id} onClick={handleIgnoredAnchorPointClick} />
      </TooltipWithArrow>
    )
  }

  if (isSelectedEntityOriginAnchor) {
    return <AnchorPointHotspotSelectedOrigin id={id} onClick={handleIgnoredAnchorPointClick} />
  }

  if (isSelectedEntityLocalAnchor) {
    return <AnchorPointHotspotSelectedLocal id={id} onClick={handleIgnoredAnchorPointClick} />
  }

  if (isLocalToSelectedEntity) {
    return <AnchorPointHotspotLocal id={id} onClick={handleLocalAnchorPointClick} />
  }

  return <AnchorPointHotspotDefault id={id} onClick={handleOriginAnchorPointClick} />
}
